import React from 'react';
import { Navigate } from 'react-router-dom';
import { auth } from '../services/firebase';
import { useSimpleAuth } from '../contexts/SimpleAuthContext';

const SimpleProtectedRoute = ({ children, roles = [], requireAuth = true }) => {
  const { user, userClaims, loading } = useSimpleAuth();

  // Log estratégico como sugirió ChatGPT
  console.log("🛡️ SimpleProtectedRoute", {
    hasUser: !!user,
    claims: !!userClaims,
    loading,
    roles,
    requireAuth
  });

  // Mostrar loading mientras se verifica la autenticación
  if (loading) {
    console.log("⏳ SimpleProtectedRoute: Mostrando loading...");
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Verificando acceso...</p>
        </div>
      </div>
    );
  }

  // Si no requiere autenticación, mostrar el componente
  if (!requireAuth) {
    return children;
  }

  // Si no está autenticado, redirigir al login
  if (!user || !userClaims) {
    console.log("🔒 SimpleProtectedRoute: Usuario no autenticado - redirigiendo a login");
    return <Navigate to="/login" replace />;
  }

  // Determinar roles
  const userRole = userClaims.role;
  const isGlobalAdmin = userClaims.globalAdmin === true || userRole === 'globalAdmin';
  const isBusinessAdmin = userClaims.isAdmin === true || userRole === 'businessAdmin';
  const isSomeAdmin = isGlobalAdmin || isBusinessAdmin;

  // Si el usuario no está aprobado, mostrar mensaje de espera (solo aplica a clientes)
  console.log("🔍 SimpleProtectedRoute: Verificando aprobación - approved:", userClaims.approved, "approvalStatus:", userClaims.approvalStatus, "isAdmin:", isSomeAdmin);

  // Para administradores no exigimos approved; para clientes sí.
  // Si el claim "approved" no existe (usuarios antiguos), NO bloqueamos.
  const isApproved = isSomeAdmin ? true : (userClaims.approved === true || typeof userClaims.approved === 'undefined');

  console.log("🔍 SimpleProtectedRoute: isApproved:", isApproved);

  if (!isApproved) {
    const handleBackToLogin = async () => {
      try { await auth.signOut(); } catch (e) { /* noop */ }
      // Redirigir de forma dura para evitar bloqueos por rutas protegidas
      window.location.replace('/login');
    };
    console.log("⏳ SimpleProtectedRoute: Usuario NO aprobado - bloqueando acceso");
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4">
            <h2 className="text-lg font-semibold mb-2">Cuenta Pendiente de Aprobación</h2>
            <p>Tu cuenta está siendo revisada por el administrador. Te notificaremos cuando esté lista.</p>
          </div>
          <button
            onClick={handleBackToLogin}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Cerrar sesión y volver al Login
          </button>
        </div>
      </div>
    );
  }

  // Si requiere roles específicos, verificar
  if (roles.length > 0) {
    const isClient = userRole === 'client';

    const hasRequiredRole = roles.some(role => {
      if (role === 'admin' && (isGlobalAdmin || isBusinessAdmin)) return true;
      if (role === 'globalAdmin' && isGlobalAdmin) return true;
      if (role === 'businessAdmin' && isBusinessAdmin) return true;
      if (role === 'client' && isClient) return true;
      return false;
    });

    if (!hasRequiredRole) {
      // Redirigir según el tipo de usuario
      if (isGlobalAdmin) {
        return <Navigate to="/global-admin" replace />;
      } else if (isBusinessAdmin) {
        return <Navigate to="/admin" replace />;
      } else if (isClient) {
        return <Navigate to={`/booking/${userClaims.tenantId}`} replace />;
      } else {
        return <Navigate to="/login" replace />;
      }
    }
  }

  // Si todo está bien, mostrar el componente
  console.log("✅ SimpleProtectedRoute: Devolviendo children (AdminDayDashboard)");
  return children;
};

export default SimpleProtectedRoute;
